import type { EnumDef } from '@clawhire/shared';
import {
  AgreementStatus,
  FileKind,
  GoDecision,
  PartnerType,
  PoolStatus,
  PortalStatus,
  PortalStatusDetail,
  PositionStage,
  SubmissionStatus,
  WaitingOn,
  WorkModel,
} from '@clawhire/shared';

/** Colour tones used across badges, board columns and stat hints. */
export type Tone = 'neutral' | 'info' | 'violet' | 'success' | 'warning' | 'danger' | 'muted';

export const TONE_CLASSES: Record<Tone, string> = {
  neutral: 'border-transparent bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  muted: 'border-transparent bg-muted text-muted-foreground',
  info: 'border-transparent bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
  violet: 'border-transparent bg-primary/15 text-primary dark:bg-primary/20',
  success: 'border-transparent bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  warning: 'border-transparent bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  danger: 'border-transparent bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
};

/** Dot colours (board column headers, legends) for the same tones. */
export const TONE_DOT_CLASSES: Record<Tone, string> = {
  neutral: 'bg-slate-400',
  muted: 'bg-muted-foreground/60',
  info: 'bg-sky-500',
  violet: 'bg-primary',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-rose-500',
};

export type ToneMap = Record<string, Tone>;

export const POSITION_STAGE_TONES: Record<PositionStage, Tone> = {
  NEW: 'neutral',
  SOURCING: 'info',
  SUBMITTED: 'violet',
  INTERVIEWING: 'violet',
  OFFER: 'warning',
  FILLED: 'success',
  LOST: 'danger',
  DROPPED: 'muted',
};

export const SUBMISSION_STATUS_TONES: Record<SubmissionStatus, Tone> = {
  SCREENING: 'neutral',
  SUBMITTED: 'info',
  HR_INTERVIEW: 'violet',
  CLIENT_INTERVIEW: 'violet',
  OFFER: 'warning',
  PLACED: 'success',
  REJECTED: 'danger',
  WITHDRAWN: 'muted',
};

export const POOL_STATUS_TONES: Record<PoolStatus, Tone> = {
  ACTIVE: 'success',
  ON_HOLD: 'warning',
  PLACED: 'violet',
  INACTIVE: 'muted',
};

export const GO_DECISION_TONES: Record<GoDecision, Tone> = {
  PENDING: 'neutral',
  PROPOSED_GO: 'info',
  PROPOSED_NO_GO: 'warning',
  GO: 'success',
  NO_GO: 'danger',
  CHECK_FIRST: 'warning',
};

export const PORTAL_STATUS_TONES: Record<PortalStatus, Tone> = { OPEN: 'success', CLOSED: 'muted' };

export const PORTAL_STATUS_DETAIL_TONES: Record<PortalStatusDetail, Tone> = {
  OPEN: 'success',
  ENOUGH_CVS: 'warning',
  CLOSED: 'muted',
  NOT_FOUND: 'danger',
};

export const PARTNER_TYPE_TONES: Record<PartnerType, Tone> = {
  INTERMEDIARY: 'violet',
  SUPPLIER: 'info',
  FREELANCER: 'neutral',
};

export const AGREEMENT_STATUS_TONES: Record<AgreementStatus, Tone> = {
  NONE: 'muted',
  NDA_SENT: 'warning',
  NDA_SIGNED: 'info',
  FRAMEWORK_SIGNED: 'success',
};

export const WAITING_ON_TONES: Record<WaitingOn, Tone> = {
  CLIENT: 'info',
  INTERMEDIARY: 'violet',
  SUPPLIER: 'neutral',
  CANDIDATE: 'warning',
  US: 'danger',
};

export const WORK_MODEL_TONES: Record<WorkModel, Tone> = {
  REMOTE: 'success',
  HYBRID: 'info',
  ON_SITE: 'warning',
};

export const FILE_KIND_TONES: Record<FileKind, Tone> = {
  CV_ORIGINAL: 'neutral',
  PROFILE_PDF: 'violet',
  PROFILE_DOCX: 'violet',
  PROFILE_JSON: 'muted',
  JD_PDF: 'info',
  JD_DOCX: 'info',
  JD_JSON: 'muted',
  OPEN_JD_COPY: 'success',
  OTHER: 'muted',
};

/** Registry: enum definition -> tone map. Unknown enums fall back to neutral. */
const TONE_REGISTRY = new Map<EnumDef<string>, ToneMap>([
  [PositionStage as unknown as EnumDef<string>, POSITION_STAGE_TONES],
  [SubmissionStatus as unknown as EnumDef<string>, SUBMISSION_STATUS_TONES],
  [PoolStatus as unknown as EnumDef<string>, POOL_STATUS_TONES],
  [GoDecision as unknown as EnumDef<string>, GO_DECISION_TONES],
  [PortalStatus as unknown as EnumDef<string>, PORTAL_STATUS_TONES],
  [PortalStatusDetail as unknown as EnumDef<string>, PORTAL_STATUS_DETAIL_TONES],
  [PartnerType as unknown as EnumDef<string>, PARTNER_TYPE_TONES],
  [AgreementStatus as unknown as EnumDef<string>, AGREEMENT_STATUS_TONES],
  [WaitingOn as unknown as EnumDef<string>, WAITING_ON_TONES],
  [WorkModel as unknown as EnumDef<string>, WORK_MODEL_TONES],
  [FileKind as unknown as EnumDef<string>, FILE_KIND_TONES],
]);

/** Tone for an enum value: from the override map, else the registry, else neutral. */
export function toneFor<T extends string>(
  def: EnumDef<T>,
  code: T | string | null | undefined,
  tones?: ToneMap,
): Tone {
  if (!code) return 'muted';
  const map = tones ?? TONE_REGISTRY.get(def as unknown as EnumDef<string>);
  return map?.[code] ?? 'neutral';
}

/** Board columns for an enum, in enum order: `boardGroupsFor(PositionStage)`. */
export function boardGroupsFor<T extends string>(
  def: EnumDef<T>,
  only?: readonly T[],
): Array<{ key: T; label: string; tone: Tone }> {
  return (only ?? def.codes).map((code) => ({ key: code, label: def.label(code), tone: toneFor(def, code) }));
}
